import cn from 'classnames';
import { Core, Layout, Localization, Typography } from 'connex-cds';
import React from 'react';
import styled from 'styled-components';
import style from './style';
import { get, find } from 'lodash';
import { useProductSetup } from '../../../../MasterDataProvider';

const Styled = styled(Layout.Container)`
  ${style}
`;

export const ListItem = ({
  item,
  idx,
  handleRowClick,
  handleDeleteClick,
  handleNoWaterAdded,
  disabled,
  paths,
  paymentMethod,
  isWaterReleaseMandatory,
  ...props
}) => {
  const productSetup = useProductSetup();
  const isDisabled = React.useMemo(() => {
    return disabled?.(item);
  }, [disabled, item]);

  const handleClick = React.useCallback(
    e => {
      e.stopPropagation();

      if (isDisabled) return;
      handleDeleteClick(item);
    },
    [handleDeleteClick, isDisabled, item]
  );

  const modifiedUOM = value => {
    const targetProduct = find(productSetup?.data, { crn: value?.item?.productRef });

    if (targetProduct?.uomCode === '/MQ/ + MTQ' || targetProduct?.uomCode === '/YQ/ + YDQ') {
      return targetProduct?.defaultQuantityPerCubicUnit?.uomCode;
    }

    return value?.quantity?.uomCode;
  };

  const handleItemClick = React.useCallback(() => {
    handleRowClick(item);
  }, [handleRowClick, item]);

  const flags = React.useMemo(() => {
    return {
      isPrimary: get(item, paths.isPrimary),
      isReasonFieldEnabled: get(item, paths.isReasonFieldEnabled) === true, // || isUndefined(get(item, paths.isReasonFieldEnabled)),
      isConcreteOnTruckFieldEnabled: get(item, paths.isConcreteOnTruckFieldEnabled) === true, //|| isUndefined(get(item, paths.isConcreteOnTruckFieldEnabled)),
      isTimeFieldEnabled: get(item, paths.isTimeFieldEnabled) === true, // || isUndefined(get(item, paths.isTimeFieldEnabled)),
      isSplitLoad: get(item, paths.isSplitLoad),
    };
  }, [
    item,
    paths.isConcreteOnTruckFieldEnabled,
    paths.isPrimary,
    paths.isReasonFieldEnabled,
    paths.isSplitLoad,
    paths.isTimeFieldEnabled,
  ]);

  const driverDidNotAddWater = React.useMemo(
    () => paths?.errors === 'waterAddedEvents' && get(item, paths.driverDidNotAdd) === true,
    [item, paths?.driverDidNotAdd, paths?.errors]
  );

  const showNoWaterInsteadOfDelete = React.useMemo(
    () =>
      paths?.errors === 'waterAddedEvents' &&
      idx === 0 &&
      Boolean(item?.waterProduct) &&
      item?.suggestedToDriver === true &&
      !driverDidNotAddWater,
    [driverDidNotAddWater, isWaterReleaseMandatory, item, idx, paths?.errors]
  );

  const showWaterRowActions = !driverDidNotAddWater;

  if (!flags.isPrimary && !flags.isSplitLoad) {
    let description = '';

    if (Array.isArray(paths.description)) {
      description = paths.description
        ?.map(path => get(item, path))
        ?.filter(Boolean)
        ?.reduce((acc, curr) => {
          if (acc === '') {
            return `${acc}${curr}`;
          }

          return acc;
        }, '');
    }

    if (!Array.isArray(paths.description)) {
      description = get(item, paths.description);
    }

    return (
      <Styled
        className={cn('list-item', { hasError: !!props.errors?.[paths.errors]?.[idx] })}
        onClick={handleItemClick}
      >
        <div className="description-delete">
          <div className="description">
            <Typography.H3 data-testid="product-value">{description}</Typography.H3>
          </div>
          {showWaterRowActions &&
            (showNoWaterInsteadOfDelete ? (
              <div className="no-water-action" onClick={e => e?.stopPropagation?.()}>
                <Core.Button
                  stringId="noWaterAdded"
                  data-testid="no-water-added-button"
                  type="primary"
                  disabled={isDisabled}
                  onClick={e => {
                    e?.stopPropagation?.();
                    if (!isDisabled) {
                      handleNoWaterAdded?.(idx);
                    }
                  }}
                />
              </div>
            ) : (
              <div className="delete" onClick={handleClick}>
                <h3>
                  <i className={cn('delete-icon fa-regular fa-trash', { disabled: isDisabled })} />
                </h3>
              </div>
            ))}
        </div>
        {get(item, paths.driverDidNotAdd) !== true ? (
          <>
            <div className="label-value">
              <div className="label">
                <Localization.Translate stringId="quantity" data-testid="quantity-header" />
              </div>
              {get(item, paths.driverDidNotAdd) !== true && (
                <Localization.Uom uom={{ ...get(item, paths.quantity), uomCode: modifiedUOM(item) }} short />
              )}
            </div>
            {flags.isReasonFieldEnabled === true && (
              <div className="label-value">
                <div className="label">
                  <Localization.Translate stringId="reason" data-testid="reason-header" />
                </div>

                {get(item, paths.reason)}
              </div>
            )}

            {flags.isConcreteOnTruckFieldEnabled === true && (
              <div className="label-value">
                <div className="label">
                  <Localization.Translate stringId="materialOnTruck" data-testid="concreteOnTruck-header" />
                </div>
                <Localization.Uom uom={get(item, paths.concreteOnTruck)} short />
              </div>
            )}
            {flags.isTimeFieldEnabled === true && (
              <div className="label-value">
                <div className="label">
                  <Localization.Translate stringId="time" data-testid="time-header" />
                </div>

                <Localization.Time date={get(item, paths.time)} />
              </div>
            )}
            {Boolean(item?.priceSummary?.unitPrice) && paymentMethod?.isCOD && (
              <div className="label-value">
                <div className="label">
                  <Localization.Translate stringId="unitPrice" data-testid="unit-price-header" />
                </div>

                {get(item, paths.unitPrice)}
              </div>
            )}
          </>
        ) : (
          <div className={'didNotAdd'}>
            <Localization.Translate
              stringId={paths?.errors === 'waterAddedEvents' ? 'noWaterAdded' : 'driverDidNotAdd'}
              data-testid="driverDidNotAddText"
            />
          </div>
        )}
      </Styled>
    );
  }
};
